"use client";

import {
  Children,
  type FC,
  isValidElement,
  type PropsWithChildren,
  useState,
} from "react";
import { cn } from "../../utils";
import { Carousel, type CarouselApi, CarouselContent } from "../carousel";
import useAutoplay from "./hooks/useAutoplay";
import useRevealedSlides from "./hooks/useRevealedSlides";
import PromoCarouselArrow from "./PromoCarouselArrow";
import PromoCarouselDots from "./PromoCarouselDots";
import PromoCarouselSlide from "./PromoCarouselSlide";

type Props = PropsWithChildren<{
  label: string;
  previousLabel?: string;
  nextLabel?: string;
  slideLabel?: string;
  autoplayDelay?: number;
  dir?: "ltr" | "rtl";
  className?: string;
}>;

const PromoCarousel: FC<Props> = ({
  children,
  label,
  previousLabel = "Previous slide",
  nextLabel = "Next slide",
  slideLabel = "Go to slide",
  autoplayDelay = 5000,
  dir = "ltr",
  className,
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const plugins = useAutoplay(api, autoplayDelay);
  const revealed = useRevealedSlides(api);
  const slides = Children.toArray(children).filter(isValidElement);

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: "start",
        direction: dir,
        loop: true,
        inViewThreshold: 0.01,
      }}
      plugins={plugins}
      dir={dir}
      aria-label={label}
      className={cn("group/promo-carousel @container -mt-2", className)}
    >
      <CarouselContent className="-ml-3 pt-2 will-change-auto">
        {slides.map((slide, index) => (
          <PromoCarouselSlide key={slide.key} revealed={revealed.has(index)}>
            {slide}
          </PromoCarouselSlide>
        ))}
      </CarouselContent>
      <PromoCarouselArrow direction="previous" label={previousLabel} />
      <PromoCarouselArrow direction="next" label={nextLabel} />
      <PromoCarouselDots count={slides.length} label={slideLabel} />
    </Carousel>
  );
};

export default PromoCarousel;
